import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import CrimsonLogo from '../components/CrimsonLogo';
import { submitLead } from '../firebase/leads';
import { PILOT_GUARANTEE } from './public/publicContent';
import usePageMeta from './public/usePageMeta';

const DEPT_SIZES = ['1–10', '11–25', '26–50', '51–100', '100+'];

export default function EnrollPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isPilot = params.get('intent') === 'pilot';
  usePageMeta({
    title: isPilot ? 'Request a Spectra Pilot | CrimsonScan' : 'Talk to Us | CrimsonScan Spectra',
    description: 'Tell us the material your team needs. Three-month paid pilots of Spectra with a success test agreed in writing before day one.',
    path: '/enroll',
  });

  const [form, setForm] = useState({
    name: '', email: '', company: '', deptSize: '', useCase: '', phone: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [error, setError]           = useState('');

  const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

  const valid = form.name && form.email && form.company && form.deptSize && form.useCase;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await submitLead(form, isPilot ? 'pilot_request' : 'enroll_page');
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again or email support@crimsonscan.com.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    background: '#0a1120',
    border: '1px solid #1e3a5f',
    borderRadius: 6,
    color: '#e2e8f0',
    padding: '10px 12px',
    fontSize: 13,
    outline: 'none',
    width: '100%',
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#060d18',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
    }}>
      <button
        onClick={() => (window.history.length > 2 ? navigate(-1) : navigate('/'))}
        style={{ position: 'fixed', top: 20, left: 20, background: 'none', border: 'none', color: '#475569', fontSize: 13, cursor: 'pointer', padding: 0 }}
      >
        ← Back
      </button>

      <div style={{ width: '100%', maxWidth: 480 }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <CrimsonLogo size={44} />
          <h1 style={{ color: '#e2e8f0', fontWeight: 700, fontSize: '1.4rem', marginTop: 12, marginBottom: 4 }}>
            {isPilot ? 'Request a pilot' : 'Talk to us'}
          </h1>
          <p style={{ color: '#94a3b8', fontSize: 14, margin: 0 }}>
            {isPilot
              ? 'Tell us about your team and the material you need. We reply within one business day with pilot terms.'
              : 'Enterprise deployments, custom models, more than 5 seats, or anything else: tell us about your team and we reply within one business day.'}
          </p>
          {isPilot && (
            <p style={{ color: '#4ade80', fontSize: 13, margin: '10px 0 0' }}>{PILOT_GUARANTEE}</p>
          )}
        </div>

        {submitted ? (
          <div style={{
            background: '#0f1923',
            border: '1px solid #4ade8044',
            borderRadius: 12,
            padding: '36px 32px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>✓</div>
            <h2 style={{ color: '#4ade80', fontWeight: 700, fontSize: '1.1rem', marginBottom: 8 }}>
              We'll be in touch soon
            </h2>
            <p style={{ color: '#94a3b8', fontSize: 14, marginBottom: 24 }}>
              Thanks, {form.name.split(' ')[0]}. Someone from our team will reach out to {form.email} within one business day.
            </p>
            <button
              onClick={() => navigate('/login')}
              style={{ background: 'none', border: '1px solid #334155', borderRadius: 6, color: '#94a3b8', fontSize: 13, padding: '8px 20px', cursor: 'pointer' }}
            >
              Back to Sign In
            </button>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            style={{
              background: '#0f1923',
              border: '1px solid #1e3a5f',
              borderRadius: 12,
              padding: '28px 32px',
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
            }}
          >
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
                <label style={{ color: '#94a3b8', fontSize: 12 }}>Full Name *</label>
                <input type="text" placeholder="Jane Smith" value={form.name} onChange={set('name')} required style={inputStyle} />
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
                <label style={{ color: '#94a3b8', fontSize: 12 }}>Work Email *</label>
                <input type="email" placeholder="jane@company.com" value={form.email} onChange={set('email')} required style={inputStyle} />
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <label style={{ color: '#94a3b8', fontSize: 12 }}>Company / Organisation *</label>
              <input type="text" placeholder="Acme Research Inc." value={form.company} onChange={set('company')} required style={inputStyle} />
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
                <label style={{ color: '#94a3b8', fontSize: 12 }}>Department Size *</label>
                <select value={form.deptSize} onChange={set('deptSize')} required style={{ ...inputStyle, cursor: 'pointer' }}>
                  <option value="">Select…</option>
                  {DEPT_SIZES.map(s => <option key={s} value={s}>{s} people</option>)}
                </select>
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
                <label style={{ color: '#94a3b8', fontSize: 12 }}>Phone (optional)</label>
                <input type="tel" placeholder="+1 555 000 0000" value={form.phone} onChange={set('phone')} style={inputStyle} />
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <label style={{ color: '#94a3b8', fontSize: 12 }}>What are you trying to discover? *</label>
              <textarea
                placeholder="e.g. A stable lithium-containing solid electrolyte without cobalt or rare earths…"
                value={form.useCase}
                onChange={set('useCase')}
                required
                rows={3}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </div>

            {error && <p style={{ color: '#f87171', fontSize: 12, margin: 0 }}>{error}</p>}

            <button
              type="submit"
              disabled={submitting || !valid}
              style={{
                background: '#f43f5e',
                border: 'none',
                borderRadius: 8,
                color: '#fff',
                fontWeight: 700,
                fontSize: 14,
                padding: '12px 0',
                cursor: submitting || !valid ? 'not-allowed' : 'pointer',
                opacity: submitting || !valid ? 0.6 : 1,
                marginTop: 4,
              }}
            >
              {submitting ? 'Submitting…' : (isPilot ? 'Request a pilot' : 'Request Access')}
            </button>

            <p style={{ color: '#334155', fontSize: 11, textAlign: 'center', margin: 0 }}>
              No commitment required. We'll reach out to discuss fit and pricing.
            </p>
          </form>
        )}

        <p style={{ color: '#475569', fontSize: 12, textAlign: 'center', marginTop: 20 }}>
          Already have an account?{' '}
          <button
            onClick={() => navigate('/login')}
            style={{ background: 'none', border: 'none', color: '#38bdf8', cursor: 'pointer', fontSize: 12, padding: 0 }}
          >
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
}
